import { defineMessages } from 'react-intl';

import type { AgentPromptPreset } from '../config';

// Message ids predate the package (they lived in the AgentChat config);
// they are kept verbatim so the existing 14-language translations continue
// to resolve without a re-translation pass.
export const patientPageMessages = defineMessages({
  presetTreatmentPlanTitle: {
    id: 'patientAgent.presetTreatmentPlanTitle',
    defaultMessage: 'Treatment plan',
  },
  presetTreatmentPlanDetail: {
    id: 'patientAgent.presetTreatmentPlanDetail',
    defaultMessage: 'Findings, imaging and staged plan from the latest study',
  },
  presetTreatmentPlanPrompt: {
    id: 'patientAgent.presetTreatmentPlanPrompt',
    defaultMessage:
      'Create and publish a patient document "Treatment plan" for this patient — not just a chat answer.\n\nGather the evidence from the latest study, AI report, photos, and available patient records. Write the complete patient document Markdown body: the reason for treatment, findings and measurements with dates, imaging interpretation, the staged treatment plan, and limitations or follow-up considerations.\n\nCreate the document with the patient-document workflow, attach the relevant study images, and publish it.\n\nReply in chat with only a concise summary of the created document. Never paste the document body into the chat.',
  },
  presetReferralLetterTitle: {
    id: 'patientAgent.presetReferralLetterTitle',
    defaultMessage: 'Referral letter',
  },
  presetReferralLetterDetail: {
    id: 'patientAgent.presetReferralLetterDetail',
    defaultMessage: 'Summary of the case for a specialist',
  },
  presetReferralLetterPrompt: {
    id: 'patientAgent.presetReferralLetterPrompt',
    defaultMessage:
      'Create and publish a patient document "Referral letter" for this patient — not just a chat answer.\n\nWrite the complete patient document Markdown body: the reason for referral, the relevant clinical findings with dates, supporting images, what the specialist should evaluate, and any recommendations.\n\nCreate the document with the patient-document workflow, attach the relevant study images, and publish it.\n\nReply in chat with only a concise summary of the created document. Never paste the document body into the chat.',
  },
  presetFollowUpTitle: {
    id: 'patientAgent.presetFollowUpTitle',
    defaultMessage: 'Patient follow-up message',
  },
  presetFollowUpDetail: {
    id: 'patientAgent.presetFollowUpDetail',
    defaultMessage: 'Plain-language recap of what happens next',
  },
  presetFollowUpPrompt: {
    id: 'patientAgent.presetFollowUpPrompt',
    defaultMessage:
      'Draft a patient-friendly follow-up message based on the consultation and available materials.\n\nOutput:\n1. Short summary of what was discussed\n2. Simple explanation of the next step\n3. Reassuring closing sentence\n4. Call to action to schedule or continue communication\n\nUse calm, non-technical language.\nDo not mention diagnoses unless they were explicitly confirmed by the clinician.\nDo not create internal tasks.\nReply with the message in the chat. Do not create a patient document.',
  },
  presetCaseSnapshotTitle: {
    id: 'patientAgent.presetCaseSnapshotTitle',
    defaultMessage: 'Case snapshot',
  },
  presetCaseSnapshotPrompt: {
    id: 'patientAgent.presetCaseSnapshotPrompt',
    defaultMessage:
      'Create a concise case snapshot for this patient.\n\nUse all available data: images, AI reports, photos, consultation transcript, notes, and appointments.\n\nOutput:\n1. Main reason for visit\n2. Available materials\n3. Key facts from the consultation\n4. AI/image findings for clinician review\n5. Patient concerns or goals\n6. Current case status\n\nDo not diagnose, recommend treatment, or create a treatment plan.',
  },
  presetDataCompletenessTitle: {
    id: 'patientAgent.presetDataCompletenessTitle',
    defaultMessage: 'Data completeness',
  },
  presetDataCompletenessPrompt: {
    id: 'patientAgent.presetDataCompletenessPrompt',
    defaultMessage:
      'Check whether this patient case has all necessary materials for a complete clinical review.\n\nOutput:\n1. Available records\n2. Missing records\n3. Incomplete or poor-quality materials\n4. Conflicts between transcript, photos, and reports\n5. Suggested data collection tasks\n\nFocus only on case completeness. Do not summarize the whole case or suggest treatment.',
  },
  presetCompareStudyTitle: {
    id: 'patientAgent.presetCompareStudyTitle',
    defaultMessage: 'Compare with previous study',
  },
  presetCompareStudyPrompt: {
    id: 'patientAgent.presetCompareStudyPrompt',
    defaultMessage:
      'Compare the latest study with the previous study for this patient.\n\nOutput:\n1. Studies being compared, with acquisition dates\n2. New findings in the latest study\n3. Findings that changed: worsened, improved, or resolved\n4. Measurements that changed, with the numeric delta\n5. What the clinician should review\n\nCalculate deltas only from structured values. Preserve acquisition chronology. Answer in the chat; do not create a patient document.',
  },
  presetNextAppointmentTitle: {
    id: 'patientAgent.presetNextAppointmentTitle',
    defaultMessage: 'Next appointment prep',
  },
  presetNextAppointmentPrompt: {
    id: 'patientAgent.presetNextAppointmentPrompt',
    defaultMessage:
      "Prepare the team for this patient's next appointment.\n\nOutput:\n1. Purpose of the next visit\n2. Questions the clinician should clarify\n3. Items the assistant/coordinator should prepare\n4. Materials to have ready\n5. Suggested visit agenda\n\nDo not summarize the full case. Do not create patient messages. Do not recommend treatment.",
  },
});

/** Patient profile page — the original full-case preset set. */
export const patientPagePresets = {
  write: [
    {
      id: 'patient.treatmentPlan',
      group: 'write',
      title: patientPageMessages.presetTreatmentPlanTitle,
      detail: patientPageMessages.presetTreatmentPlanDetail,
      prompt: patientPageMessages.presetTreatmentPlanPrompt,
    },
    {
      id: 'patient.referralLetter',
      group: 'write',
      title: patientPageMessages.presetReferralLetterTitle,
      detail: patientPageMessages.presetReferralLetterDetail,
      prompt: patientPageMessages.presetReferralLetterPrompt,
    },
    {
      id: 'patient.followUp',
      group: 'write',
      title: patientPageMessages.presetFollowUpTitle,
      detail: patientPageMessages.presetFollowUpDetail,
      prompt: patientPageMessages.presetFollowUpPrompt,
    },
  ] satisfies AgentPromptPreset[],
  understand: [
    {
      id: 'patient.caseSnapshot',
      group: 'understand',
      title: patientPageMessages.presetCaseSnapshotTitle,
      prompt: patientPageMessages.presetCaseSnapshotPrompt,
    },
    {
      id: 'patient.dataCompleteness',
      group: 'understand',
      title: patientPageMessages.presetDataCompletenessTitle,
      prompt: patientPageMessages.presetDataCompletenessPrompt,
    },
    {
      id: 'patient.compareStudy',
      group: 'understand',
      title: patientPageMessages.presetCompareStudyTitle,
      prompt: patientPageMessages.presetCompareStudyPrompt,
    },
    {
      id: 'patient.nextAppointment',
      group: 'understand',
      title: patientPageMessages.presetNextAppointmentTitle,
      prompt: patientPageMessages.presetNextAppointmentPrompt,
    },
  ] satisfies AgentPromptPreset[],
};
